import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';

// Tipos de datos para los reportes
interface ReportData {
    title: string;
    subtitle: string;
    date: string;
    type: 'commercial' | 'technical' | 'health' | 'efficiency';
    metrics: any;
    data: any[];
}

const COMPANY_LOGO = "/images/farmtrace/milk_production_icon.png"; // Usaremos el icono como logo por ahora
const COMPANY_NAME = "FarmTrace Analytics";
const COMPANY_ADDRESS = "Av. Innovación 123, Santiago, Chile";

// Función auxiliar para formatear moneda
const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP' }).format(value);
};

// --- GENERADOR PDF ---
export const generatePDFReport = (reportData: ReportData) => {
    const doc = new jsPDF();

    let primaryColor = [41, 128, 185]; // Default Blue
    if (reportData.type === 'commercial') primaryColor = [41, 128, 185]; // Blue
    if (reportData.type === 'technical') primaryColor = [39, 174, 96]; // Green
    if (reportData.type === 'health') primaryColor = [231, 76, 60]; // Red
    if (reportData.type === 'efficiency') primaryColor = [142, 68, 173]; // Purple

    // 1. Encabezado Corporativo
    doc.setFillColor(primaryColor[0], primaryColor[1], primaryColor[2]);
    doc.rect(0, 0, 210, 40, 'F');

    doc.setTextColor(255, 255, 255);
    doc.setFontSize(24);
    doc.setFont('helvetica', 'bold');
    doc.text(reportData.title.toUpperCase(), 15, 20);

    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text(reportData.subtitle, 15, 30);

    doc.setFontSize(10);
    doc.text(COMPANY_NAME, 150, 15);
    doc.text(reportData.date, 150, 22);
    doc.text("Confidencial", 150, 29);

    let yPos = 50;

    // 2. Resumen de Métricas (KPIs)
    doc.setTextColor(50, 50, 50);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text("Resumen Ejecutivo", 15, yPos);
    yPos += 10;

    let kpiHeaders: string[][] = [];
    let kpiData: string[][] = [];

    switch (reportData.type) {
        case 'commercial':
            kpiHeaders = [['Ingresos Totales', 'Margen', 'Costo/Litro', 'ROI']];
            kpiData = [[
                formatCurrency(reportData.metrics.revenue || 0),
                `${reportData.metrics.margin?.toFixed(1)}%`,
                formatCurrency(reportData.metrics.costPerLiter || 0),
                `${reportData.metrics.roi?.toFixed(1)}%`
            ]];
            break;
        case 'technical':
            kpiHeaders = [['Producción Total', 'Promedio Diario', 'Salud Hato', 'Eficiencia']];
            kpiData = [[
                `${reportData.metrics.totalProduction?.toFixed(0)} L`,
                `${reportData.metrics.avgDaily?.toFixed(0)} L`,
                `${reportData.metrics.healthScore?.toFixed(0)}%`,
                `${reportData.metrics.efficiency?.toFixed(0)}%`
            ]];
            break;
        case 'health':
            kpiHeaders = [['Índice Salud', 'Total Animales', 'En Tratamiento', 'Alertas']];
            kpiData = [[
                `${reportData.metrics.healthScore?.toFixed(0)}%`,
                `${reportData.metrics.totalAnimals || 0}`,
                `${reportData.metrics.inTreatment || 0}`,
                `${reportData.metrics.alerts || 0}`
            ]];
            break;
        case 'efficiency':
            kpiHeaders = [['Eficiencia Global', 'Costo/Litro', 'Rentabilidad', 'Conv. Alimento']];
            kpiData = [[
                `${reportData.metrics.efficiency?.toFixed(0)}%`,
                formatCurrency(reportData.metrics.costPerLiter || 0),
                `${reportData.metrics.profitMargin?.toFixed(1)}%`,
                `${reportData.metrics.feedConversion?.toFixed(1) || 1.2}`
            ]];
            break;
    }

    autoTable(doc, {
        startY: yPos,
        head: kpiHeaders,
        body: kpiData,
        theme: 'grid',
        headStyles: { fillColor: primaryColor as any, textColor: 255, fontStyle: 'bold' },
        styles: { fontSize: 12, cellPadding: 6, halign: 'center' },
    });

    yPos = (doc as any).lastAutoTable.finalY + 15;

    // 3. Detalle de Datos
    doc.setFontSize(14);
    doc.text("Detalle de Operaciones", 15, yPos);
    yPos += 5;

    let tableHeaders: string[][] = [];
    let tableBody: any[][] = [];

    switch (reportData.type) {
        case 'commercial':
            tableHeaders = [['Fecha', 'Concepto', 'Cantidad', 'Precio Unit.', 'Total']];
            tableBody = reportData.data.map(item => [
                new Date(item.date).toLocaleDateString(),
                item.concept || 'Venta Leche',
                item.quantity,
                formatCurrency(item.unitPrice),
                formatCurrency(item.total)
            ]);
            break;
        case 'technical':
            tableHeaders = [['Fecha', 'Animal ID', 'Evento', 'Medición', 'Estado']];
            tableBody = reportData.data.map(item => [
                new Date(item.date).toLocaleDateString(),
                item.animalId,
                item.event || 'Ordeño',
                item.measurement,
                item.status || 'Normal'
            ]);
            break;
        case 'health':
            tableHeaders = [['ID Animal', 'Estado', 'Último Control', 'Diagnóstico', 'Acción']];
            tableBody = reportData.data.map(item => [
                item.animalId,
                item.status,
                new Date(item.lastCheck).toLocaleDateString(),
                item.diagnosis || 'Sin hallazgos',
                item.action || 'Monitoreo'
            ]);
            break;
        case 'efficiency':
            tableHeaders = [['Indicador', 'Valor Actual', 'Objetivo', 'Desviación', 'Estado']];
            tableBody = reportData.data.map(item => [
                item.indicator,
                item.currentValue,
                item.targetValue,
                item.deviation,
                item.status
            ]);
            break;
    }

    autoTable(doc, {
        startY: yPos,
        head: tableHeaders,
        body: tableBody,
        theme: 'striped',
        headStyles: { fillColor: [100, 100, 100], textColor: 255 },
        styles: { fontSize: 10 },
        alternateRowStyles: { fillColor: [245, 245, 245] }
    });

    // 4. Pie de Página
    const pageCount = (doc as any).internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(150);
        doc.text(`Generado por FarmTrace Analytics - Página ${i} de ${pageCount}`, 105, 290, { align: 'center' });
    }

    doc.save(`FarmTrace_${reportData.type}_${new Date().getTime()}.pdf`);
};

// --- GENERADOR EXCEL ---
export const generateExcelReport = (reportData: ReportData) => {
    const wb = XLSX.utils.book_new();

    // Hoja 1: Resumen
    const summaryData = [
        ["REPORTE CORPORATIVO FARMTRACE"],
        [`Título: ${reportData.title}`],
        [`Fecha: ${reportData.date}`],
        [],
        ["MÉTRICAS CLAVE"],
        ...Object.entries(reportData.metrics).map(([k, v]) => [k, v])
    ];

    const wsSummary = XLSX.utils.aoa_to_sheet(summaryData);
    XLSX.utils.book_append_sheet(wb, wsSummary, "Resumen");

    // Hoja 2: Datos Detallados
    const wsData = XLSX.utils.json_to_sheet(reportData.data);
    XLSX.utils.book_append_sheet(wb, wsData, "Datos Detallados");

    XLSX.writeFile(wb, `FarmTrace_${reportData.type}_${new Date().getTime()}.xlsx`);
};
